/**
 * Subscription tier feature gates.
 *
 * Single source of truth — never inline tier comparisons in components.
 * All callers read from SubscriptionContext and pass the relevant fields.
 *
 * Tiers:
 *   - 'free'    : lifetime, 1 project slot, TRY/EUR/USD only, no image upload, general report only
 *   - 'premium' : 5 slots, all features
 */

export type Tier = 'free' | 'premium';

export const FREE_CURRENCIES: readonly string[] = ['TRY', 'EUR', 'USD'];
export const FREE_PROJECT_LIMIT = 1;
export const PREMIUM_PROJECT_LIMIT = 5;

interface TierStatus {
  tier?: Tier | string | null;
}

/**
 * Normalises any incoming string to a known tier, defaulting to 'free' when
 * the value is missing or unrecognised. Keeps gates fail-closed for premium
 * features while letting brand-new users (no row yet) use the app.
 */
export function resolveTier(status: TierStatus | null | undefined): Tier {
  return status?.tier === 'premium' ? 'premium' : 'free';
}

export function isPremium(status: TierStatus | null | undefined): boolean {
  return resolveTier(status) === 'premium';
}

/** New image uploads are premium-only. Viewing existing images is not gated. */
export function canUploadImages(status: TierStatus | null | undefined): boolean {
  return isPremium(status);
}

/**
 * Currency selection.
 *
 * Free users may select TRY / EUR / USD only. The `currentValue` exception
 * lets an existing project loaded with e.g. GBP keep rendering that value
 * in its own dropdown — switching away from a legacy currency is one-way.
 */
export function canSelectCurrency(
  code: string,
  status: TierStatus | null | undefined,
  currentValue?: string | null,
): boolean {
  if (isPremium(status)) return true;
  if (currentValue && code === currentValue) return true;
  return FREE_CURRENCIES.includes(code);
}

/** Detailed reports are premium-only. General reports are free for everyone. */
export function canGenerateDetailedReport(status: TierStatus | null | undefined): boolean {
  return isPremium(status);
}

/** Active-project ceiling for the user's tier. */
export function projectLimitFor(status: TierStatus | null | undefined): number {
  return isPremium(status) ? PREMIUM_PROJECT_LIMIT : FREE_PROJECT_LIMIT;
}
